import { AddRounded, AutoFixHighRounded, Check, CheckCircleOutlineRounded, CheckRounded, CloseRounded, ConstructionRounded, DeleteForeverOutlined, EditOutlined, HighlightOffRounded } from '@mui/icons-material';
import { Box, Button, MenuItem, OutlinedInput, Toolbar, Typography, TypographyProps, alpha } from '@mui/material';
import { ReactNode, useRef, useState } from 'react';
import route from '../../Router';
import { FormApiRef, ListApiRef } from '../../components/data/ApiRef';
import DataTable, { ColumnData, DataTableProps } from '../../components/data/DataTable';
import DataView from '../../components/data/DataView';
import TablePaper from '../../components/form/TablePaper';
import { RequestStatus, defaultRequestStatusTitle, requestStatuses } from '../../models/Enums';
import { hasKey } from '../../models/Identity';
import Proxy from '../../models/Proxy';
import { Bill, ProvidedService, Request } from '../../models/Types';
import { getTheme } from '../../theme';
import { isMobile } from '../../utils';
import { psBillCollumns } from '../bill';
import { billParse } from '../bill/BillForm';
import HouseCombo, { houseStore } from '../houses/HouseCombo';
import InhabitantCombo, { inhabitantStore } from '../inhabitants/InhabitantCombo';
import ServiceCombo, { serviceStore, serviceTextRenderer } from '../project/service/ServiceCombo';
import { MobileRequestForm, RequestForm, requestParse } from './RequestForm';
import RequestList, { noDataRenderer } from './RequestList';

const colorAlpha = .5;
const lightAlpha = .25;

route.register('service', undefined, undefined, x => parseInt(x));

export const requestStatusIcons: { [status: keyof RequestStatus]: () => ReactNode } = {
    [RequestStatus.Implementing]: () => <ConstructionRounded />,
    [RequestStatus.Completed]: () => <CheckRounded />,
    [RequestStatus.Done]: () => <CheckRounded />,
    [RequestStatus.Canceled]: () => <CloseRounded />,
    [RequestStatus.Rejected]: () => <CloseRounded />
}
export const requestStatusMobileIcons: { [status: keyof RequestStatus]: () => ReactNode } = {
    [RequestStatus.Completed]: () => <CheckCircleOutlineRounded color="success" fontSize="small" />,
    [RequestStatus.Done]: () => <CheckRounded color="success" fontSize="small" />,
    [RequestStatus.Canceled]: () => <HighlightOffRounded color="error" fontSize="small" />,
    [RequestStatus.Rejected]: () => <CheckCircleOutlineRounded color="success" fontSize="small" />
}
export const requestStatusColors: { [status: keyof RequestStatus]: string } = {
    [RequestStatus.Analyze]: alpha(getTheme().palette.warning.light, colorAlpha),
    [RequestStatus.Planing]: alpha(getTheme().palette.warning.light, colorAlpha),
    [RequestStatus.Implementing]: alpha(getTheme().palette.warning.light, colorAlpha),
    [RequestStatus.Rejected]: alpha(getTheme().palette.error.light, colorAlpha),
    [RequestStatus.Done]: alpha(getTheme().palette.success.main, colorAlpha),
    [RequestStatus.Completed]: alpha(getTheme().palette.success.light, colorAlpha),
    [RequestStatus.Canceled]: alpha(getTheme().palette.error.light, colorAlpha)
}
export const defaultRequestStatusColor = alpha('#FFF0C8', colorAlpha);
export const defaultRequestStatusLightColor = alpha('#FFF0C8', lightAlpha);
export const requestStatusLightColors: { [status: keyof RequestStatus]: string } = {}
for (var i in requestStatusColors) {
    requestStatusLightColors[i] = alpha(requestStatusColors[i], lightAlpha)
}

export function RequestStatusChip({ status, sx, ...props }: { status?: RequestStatus } & TypographyProps) {
    return <Typography
        variant="overline"
        sx={{
            textTransform: 'none',
            backgroundColor: requestStatusLightColors[status] || defaultRequestStatusLightColor,
            px: 1, py: .75,
            borderRadius: 1,
            ...sx
        }} {...props}>{requestStatuses[status] || defaultRequestStatusTitle}</Typography>
}

export const requestColumns: ColumnData<Request>[] = [{
    label: 'Наименование',
    name: 'Name',
    routerLink: true
}, {
    label: 'Услуги',
    name: 'ServiceIds',
    type: 'array',
    store: serviceStore,
    filter: (onChange) => <ServiceCombo onChange={e => onChange(e.target.value)} />,
    renderer: (val, obj) => (val as number[])?.map(v => serviceStore.data?.filter(x => x.Id == v).map(serviceTextRenderer)).join(', ')
}, {
    label: 'Адреса',
    name: 'HouseIds',
    type: 'array',
    store: houseStore,
    filter: (onChange) => <HouseCombo onChange={e => onChange(e.target.value)} />,
    renderer: (val, obj) => (val as number[])?.map(v => houseStore.data?.filter(x => x.Id == v).map(x => x.Value)).join(', ')
}, {
    label: 'Дата',
    name: 'CreateDate',
    filter: true,
    type: 'date',
}, {
    label: 'Статус',
    name: 'Status',
    filter: true,
    renderer: (x: RequestStatus) => <RequestStatusChip status={x} />
}, {
    label: 'Заявители',
    name: 'ApplicantId',
    store: inhabitantStore,
    filter: (onChange) => <InhabitantCombo onChange={e => onChange(e.target.value)} />,
    renderer: (val, obj) => inhabitantStore.data?.find(x => x.Id == val)?.Value
}];

const recPsCollumns = () => [{
    label: 'Услуга',
    name: 'ServiceId',
    renderer: hasKey('ProvidedService') ?
        (val, obj) => <ServiceCombo required={true} value={val} onChange={e => obj.ServiceId = parseInt(e.target.value)} /> :
        (val, obj) => serviceStore.data?.filter(x => x.Id == val).map(serviceTextRenderer)[0]
}, {
    label: 'Количество',
    name: 'Count',
    type: 'numeric',
    renderer: hasKey('ProvidedService') ? (val, obj, theme, table) => <OutlinedInput type="number" min={0} defaultValue={val} onChange={e => {
        obj.Count = parseInt(e.target.value);
        let price = serviceStore.data?.find(b => b.Id === obj.ServiceId)?.Obj?.Price;
        if (price) {
            obj.Sum = obj.Count * price;
        }
        table.forceUpdate();
    }} /> : undefined
}, {
    label: 'Сумма',
    name: 'Sum',
    type: 'numeric'
}] as ColumnData<ProvidedService>[];

function Services({ id, apiRef, tableProps }: { id: number, apiRef?: (api?: ListApiRef<ProvidedService>) => void, tableProps?: DataTableProps<ProvidedService> }) {
    const ref = useRef<ListApiRef<ProvidedService>>();
    const [saving, setSaving] = useState(false);

    function fillServices() {
        Proxy.post('ProvidedServiceFillForRequest', id).then(x => x && ref.current?.load());
    }

    function save() {
        var data = ref.current?.getData();
        setSaving(true);
        Proxy.post('ProvidedServiceSaveForRequest?id=' + id, data).then(x => setSaving(false));
    }

    const access = hasKey('ProvidedService');
    return <TablePaper>
        {access ? <Toolbar>
            <Button startIcon={<AutoFixHighRounded />} onClick={fillServices}>Заполнить услуги</Button>
            <Button startIcon={<AddRounded />} color="primary" sx={{ ml: 'auto' }}>Добавить</Button>
        </Toolbar> : <Box mt={2} />}
        <DataTable<ProvidedService>
            typeId="ProvidedService"
            sx={{ '& td': { padding: '.5rem' } }}
            showFilter={false}
            apiRef={x => { ref.current = x; apiRef && apiRef(x); }}
            columns={recPsCollumns()}
            rowSelector="simple"
            onSelectionChange={(id, t, e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            cmd={'ProvidedServiceForRequest?id=' + id}
            {...tableProps}        />
        {access ? <Toolbar>
            <Button color="primary" variant="contained" loading={saving} sx={{ ml: 'auto' }} onClick={save}>Сохранить</Button>
        </Toolbar> : null}
    </TablePaper>
}

export default function RequestView() {
    const ism = isMobile();
    const formRef = useRef<FormApiRef>();
    const billRef = useRef<ListApiRef<Bill>>();

    return <DataView<Request>
        title="Заявка"
        titleMultiple="Заявки"
        typeId="Request"
        menu={ism ? (id) => id ? [
            <MenuItem onClick={() => route.setState('edit', true)}>Изменить данные заявки<EditOutlined /></MenuItem>,
            <MenuItem onClick={() => formRef.current?.delete()}>
                <Typography color="error" variant="inherit">Удалить заявку</Typography><DeleteForeverOutlined color="error" />
            </MenuItem>
        ] : undefined : undefined}
        form={ism ?
            route.get('edit') ?
                (id, apiRef) => <RequestForm key="main" id={id} apiRef={x => { formRef.current = x; apiRef(x); }} service={route.get('service')} /> :
                (id, apiRef) => <MobileRequestForm key="main" id={id} apiRef={x => { formRef.current = x; apiRef(x); }} service={route.get('service')} /> :
            id => [{
                title: 'Основное',
                element: (id, apiRef) => <RequestForm key="main" id={id} apiRef={x => { formRef.current = x; apiRef(x); }} service={route.get('service')} />
            }, {
                title: 'Услуги',
                //icon: <HomeRepairServiceRounded />,
                disabled: !id,
                element: id => <Services id={id} />
            }, {
                title: 'Начисления',
                //icon: <Receipt />,
                disabled: !id,
                element: (id, apiRef) => <TablePaper sx={{ overflow: 'hidden', p: 0 }} >
                    {hasKey('Bill') ? <Toolbar>
                        <Button startIcon={<AutoFixHighRounded />} onClick={() => {
                            Proxy.post('BillCreateForRequest', id).then(x => {
                                if (x.success) {
                                    billRef.current?.load();
                                } else {
                                    debugger;
                                }
                            });
                        }}>Сформировать начисления</Button>
                    </Toolbar> : <Box mt={2} />}
                    <DataTable<Bill> typeId="Bill" columns={psBillCollumns} dataParser={billParse} apiRef={x => billRef.current = x} cmd={'BillForRequest?id=' + id} showFilter={false} />
                </TablePaper>
            }]}
        tableRenderer={p => ism ? <RequestList {...p} /> : <DataTable<Request> {...p} />}
        tableProps={{
            columns: requestColumns,
            dataParser: requestParse,
            noDataRenderer
        }} />;
}